import React, { useCallback, useEffect, useState } from 'react';
import { Tooltip, TooltipCoordinator, Pill } from '@frontapp/ui-kit';
import styles from './styles.module.css';
import copy from 'copy-to-clipboard';
import Typography from '@mui/material/Typography';

import { get } from './api.js';
import { findContacts, convertUNIXToDate } from './util.js';
import { track } from '@amplitude/analytics-browser';

const getColor = (status) => {
  if (status === 'active') {
    return 'rgb(60,179,113)';
  } else if (status === 'trialing') {
    return 'rgb(255, 192, 0)';
  } else if (status === 'canceled') {
    return 'rgb(211,211,211)';
  } else if (status === 'past_due') {
    return 'rgb(220,20,60)';
  }
};

function MotionContact({ latestInbound, setError, setContacts, contacts }) {
  const [showCopied, setShowCopied] = useState(false);

  const getContacts = useCallback(
    (msg) => {
      const handles = findContacts(msg);
      get('contacts', {
        contacts: handles,
        // contacts: [{ handle: 'ruoniwang1995@gmail.com', type: 'email' }], // test stripe
      })
        .then((res) => {
          console.log(res);
          // Only setContact is the conversation has not changed
          if (msg === latestInbound.current) {
            const currentContacts = {};
            for (const entry of res.data.results) {
              const email = entry.email;
              const snowflakeUser = entry.snowflakeUser;
              const stripes =
                entry.stripeCustomers != null &&
                entry.stripeCustomers.length > 0
                  ? entry.stripeCustomers.map((c) => ({
                      team: c.isTeam,
                      subscriptions: c.subscriptions,
                      link: 'https://dashboard.stripe.com/customers/' + c.id,
                    }))
                  : undefined;
              const onboarded =
                snowflakeUser != null ? snowflakeUser['onboarded'] : '';
              const name = snowflakeUser != null ? snowflakeUser['name'] : '';
              const userId =
                snowflakeUser != null ? snowflakeUser['id'] : 'No user found';
              currentContacts[email] = {
                stripes,
                onboarded,
                email,
                name,
                userId,
              };
              setContacts(currentContacts);
            }
          }
        })
        .catch((error) => {
          if (msg === latestInbound.current) {
            setError(error);
          }
        });
    },
    [latestInbound, setContacts, setError],
  );

  useEffect(() => {
    if (latestInbound.current) {
      getContacts(latestInbound.current);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [latestInbound.current]);

  const displaySubscriptions = (subs, isTeam) => {
    return subs.map((sub) => {
      return displaySubscription(sub, isTeam);
    });
  };

  const displaySubscription = (sub, isTeam) => {
    const status = sub.status;
    const trialEnd = convertUNIXToDate(sub.trial_end);
    const billingCycleAnchor = convertUNIXToDate(sub.billing_cycle_anchor);
    const canceledAt =
      sub.canceled_at != null ? convertUNIXToDate(sub.canceled_at) : null;
    const cancelAt =
      sub.cancel_at != null ? convertUNIXToDate(sub.cancel_at) : null;
    const cancelAtPeriodEnd = sub.cancel_at_period_end;

    return (
      <div className={styles.subscriptionItem}>
        <div>
          <span>{'Plan: '}</span>
          <div className={styles.subscriptionPills}>
            <Pill colors={{ backgroundColor: getColor(sub.status) }}>
              {sub.plan.interval}
            </Pill>
          </div>
          <div className={styles.subscriptionPills}>
            <Pill colors={{ backgroundColor: getColor(sub.status) }}>
              {sub.status}
            </Pill>
          </div>
        </div>
        {status === 'trialing' && (
          <>
            {cancelAtPeriodEnd ? (
              <div>
                <span>{'Canceling: '}</span>
                <span>{cancelAt}</span>
              </div>
            ) : (
              <div>
                <span>{'First bill: '}</span>
                <span>{trialEnd}</span>
              </div>
            )}
          </>
        )}
        {status === 'active' && (
          <>
            <div>
              <span>{'Started: '}</span>
              <span>{billingCycleAnchor}</span>
            </div>
            <div>
              <span>{'Billed: '}</span>
              <span>{sub.plan.amount / 100 + ' ' + sub.plan.currency}</span>
            </div>
            {cancelAtPeriodEnd && (
              <div>
                <span>{'Scheduled to cancel: '}</span>
                <span>{cancelAt}</span>
              </div>
            )}
          </>
        )}
        {status === 'canceled' && (
          <>
            <div>
              <span>{'Canceled at: '}</span>
              <span>{canceledAt}</span>
            </div>
          </>
        )}
        {isTeam && (
          <>
            <div>
              <span>{'# seats: '}</span>
              <span>{sub.quantity}</span>
            </div>
          </>
        )}
      </div>
    );
  };

  const fullContactUI =
    Object.entries(contacts).length > 0 &&
    Object.values(contacts).map((contact) => (
      <div className={styles.contactContainer}>
        <div
          className={styles.copiable}
          onClick={() => {
            track('COPY_EMAIL_CLICKED');
            copy(contact.email);
            setShowCopied(true);
            setTimeout(() => {
              setShowCopied(false);
            }, 1000);
          }}
        >
          <span>Email : </span>
          <TooltipCoordinator
            renderTooltip={() => {
              return (
                <Tooltip>{showCopied ? 'Copied!' : 'Click to copy'}</Tooltip>
              );
            }}
          >
            {contact.email}
          </TooltipCoordinator>
        </div>
        {contact.userId != null && (
          <div>
            <span className={styles.contactSubHeader}>UID : </span>
            <span>{contact.userId}</span>
          </div>
        )}
        {contact.onboarded != null && (
          <div>
            <span className={styles.contactSubHeader}>Onboarded : </span>
            <span>{contact.onboarded === true ? 'True' : 'False'}</span>
          </div>
        )}
        {contact.stripes == null && (
          <div>
            <span>No stripe customer found</span>
          </div>
        )}
        {contact.stripes != null &&
          contact.stripes.length > 0 &&
          contact.stripes.map((entry) => (
            <div className={styles.stripeCustomer}>
              <span>
                {'Stripe ' + (entry.team === true ? 'team: ' : 'individual: ')}
              </span>
              <a
                className={styles.contactText}
                href={entry.link}
                target="_blank"
                rel="noopener noreferrer"
              >
                link
              </a>
              {entry.subscriptions.data.length === 0 && (
                <div>
                  <span>No subscriptions found</span>
                </div>
              )}
              {entry.subscriptions.data.length > 0 && (
                <div>
                  <span>Subscriptions: </span>
                  {displaySubscriptions(entry.subscriptions.data, entry.team)}
                </div>
              )}
            </div>
          ))}
      </div>
    ));
  return (
    <div className={styles.container}>
      <Typography
        sx={{ fontSize: '0.85rem', lineHeight: 1.2, textAlign: 'left' }}
      >
        {fullContactUI}
      </Typography>
    </div>
  );
}

export default MotionContact;
